import { formatNumber } from '../../helpers/numberUtils';

export const formatBerlin = (iso: string) => {
  if (!iso) return '';
  // DB14 stores DATETIME as Berlin wallclock; node-app/src/database.ts configures
  // mysql2 with `timezone: '+00:00'` so the Berlin bytes get tagged as UTC. JSON
  // serialization adds the trailing Z. `new Date(iso).toLocaleString({timeZone:'Europe/Berlin'})`
  // would then double-shift +2h (CEST) — e.g. 12:45 Berlin renders as 14:45.
  // Naive `berlinNow()` strings (`YYYY-MM-DD HH:MM:SS`, no T, no Z) also encode
  // raw Berlin. In both cases the right move is to read the wallclock components
  // directly from the string. See CLAUDE.md › Timezones.
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!match) return iso;
  const [, year, month, day, hour, minute] = match;
  return `${day}.${month}.${year}, ${hour}:${minute}`;
};

export const statusChipColor = (status: string): 'default' | 'success' | 'warning' | 'error' => {
  switch (status) {
    case 'sent':
      return 'success';
    case 'scheduled':
      return 'warning';
    case 'cancelled':
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
};

// Shared table-cell render helpers used by FT SAM, External SAM, and AA history
// tables. Lead/Revenue/SOI may legitimately be NULL on rows where attribution
// hasn't landed yet — keep the NULL vs 0 visual distinction (NULL → '–',
// 0 → '0'). ETKP = Revenue × 1000 / Empfänger (classic TKP).
export const renderInt = (v: number | null | undefined): string =>
  v === null || v === undefined ? '–' : formatNumber(v, 0);

export const renderEur = (v: number | string | null | undefined): string => {
  if (v === null || v === undefined || v === '') return '–';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (!Number.isFinite(n)) return '–';
  return `${formatNumber(n, 2)} €`;
};

export const renderRate = (
  numerator: number | null | undefined,
  denominator: number | null | undefined,
  decimals = 1,
): string => {
  if (
    numerator === null ||
    numerator === undefined ||
    denominator === null ||
    denominator === undefined ||
    denominator === 0
  ) {
    return '–';
  }
  return `${formatNumber((numerator / denominator) * 100, decimals)} %`;
};

export const renderEtkp = (
  revenue: number | string | null | undefined,
  recipients: number | null | undefined,
): string => {
  if (revenue === null || revenue === undefined || revenue === '') return '–';
  if (!recipients) return '–';
  const r = typeof revenue === 'string' ? parseFloat(revenue) : revenue;
  if (!Number.isFinite(r)) return '–';
  return `${formatNumber((r * 1000) / recipients, 2)} €`;
};

export const renderType = (t: string | null | undefined): string => {
  if (t === 'asset_analysis') return 'AA';
  if (t === 'sam' || !t) return 'SAM';
  return t;
};

// Display label per external SAM client. kme sends only the 08:00 shadow
// re-send of DNV's SAM (external_sam_enabled=0) — the label carries that.
export const clientLabel = (code: string | null | undefined): string => {
  if (!code) return '–';
  const c = code.toLowerCase();
  if (c === 'kme') return 'KME (Shadow)';
  return c.toUpperCase();
};
